import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../dialog/U2000BasicDialog";
import U2000SearchCriteriaCalendar from "./U2000SearchCriteriaCalendar";
import {
  type DateCriteriaRange,
  formatIntervalDisplayValue,
  isIntervalRangeComplete,
  withIntervalPanelDefaults,
} from "./u2000DateCriteriaUtils";

export interface U2000SearchCriteriaIntervalFieldProps {
  range?: DateCriteriaRange;
  onRangeChange: (range: DateCriteriaRange) => void;
  disabled?: boolean;
  placeHolder?: string;
}

const U2000SearchCriteriaIntervalField: React.FC<U2000SearchCriteriaIntervalFieldProps> = ({
  range = {},
  onRangeChange,
  disabled = false,
  placeHolder,
}) => {
  const { t } = useTranslation("common");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateCriteriaRange>(range);
  const [calendarSessionKey, setCalendarSessionKey] = useState(0);

  const displayRange = isDialogOpen ? draftRange : range;
  const displayText = formatIntervalDisplayValue(displayRange);
  const resolvedPlaceholder = placeHolder ?? t("lblSearchCriteriaIntervalPlaceholder");

  useEffect(() => {
    if (!isDialogOpen) {
      setDraftRange(range);
    }
  }, [isDialogOpen, range]);

  function openDialog(): void {
    if (disabled) {
      return;
    }
    const todayYmd = format(new Date(), "yyyy-MM-dd");
    setDraftRange(withIntervalPanelDefaults(range, todayYmd));
    setCalendarSessionKey((k) => k + 1);
    setIsDialogOpen(true);
  }

  function closeDialog(): void {
    setIsDialogOpen(false);
  }

  function handleConfirm(): void {
    onRangeChange(draftRange);
    closeDialog();
  }

  function handleCancel(): void {
    closeDialog();
  }

  const triggerClasses = [
    "flex h-[46px] w-full cursor-pointer items-center rounded-lg border px-4 py-3 text-sm transition-all duration-200 focus-within:outline-hidden focus-within:ring-2",
    disabled ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-500" : "border-gray-300 bg-white text-gray-900 hover:border-gray-400",
    !disabled && isDialogOpen ? "border-blue-500 ring-blue-500/20" : "",
  ].join(" ");

  return (
    <>
      <div
        className={triggerClasses}
        onClick={openDialog}
        onKeyDown={(e) => {
          if (disabled) {
            return;
          }
          if (e.key === "Enter" || e.key === " " || e.key === "F8") {
            e.preventDefault();
            openDialog();
          }
        }}
        role="button"
        aria-expanded={isDialogOpen}
        aria-haspopup="dialog"
        aria-label={displayText ? `${t("lblCndInterval")}: ${displayText}` : t("lblSearchCriteriaIntervalPlaceholder")}
        tabIndex={disabled ? -1 : 0}
      >
        <span className={`flex-1 truncate text-sm ${displayText ? "" : "text-gray-400"}`} title={displayText || undefined}>
          {displayText || resolvedPlaceholder}
        </span>
        <button
          type="button"
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors duration-200 ${
            disabled ? "cursor-not-allowed text-gray-400" : "text-gray-500 hover:text-blue-600 focus:text-blue-600 focus:outline-hidden"
          }`}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            openDialog();
          }}
          aria-label={t("lblSearchCriteriaIntervalOpenCalendar")}
          tabIndex={-1}
        >
          <FaCalendarAlt className="h-4 w-4" />
        </button>
      </div>

      <U2000BasicDialog
        isOpen={isDialogOpen}
        onClose={() => handleCancel()}
        onBackdropClick={handleCancel}
        title={t("lblCndInterval")}
        width="x-large"
        contentOverflowVisible
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="overflow-y-auto p-4">
            <U2000SearchCriteriaCalendar
              key={calendarSessionKey}
              calendarCount={2}
              range={draftRange}
              onRangeChange={setDraftRange}
              disabled={disabled}
              layout="row"
            />
          </div>
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={handleCancel}
            >
              {t("btnCancel")}
            </button>
            <button
              type="button"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleConfirm}
              disabled={!isIntervalRangeComplete(draftRange)}
              title={!isIntervalRangeComplete(draftRange) ? t("errSearchCriteriaIntervalIncomplete") : undefined}
            >
              {t("btnSave")}
            </button>
          </footer>
        </div>
      </U2000BasicDialog>
    </>
  );
};

export default U2000SearchCriteriaIntervalField;
